import React, { useState, useEffect } from 'react';
import { translationsApi } from '../../lib/api/translations';

interface TranslationField {
  key: string;
  label: string;
  type: 'text' | 'textarea';
  placeholder?: string;
}

interface TranslationEditorProps {
  section: string;
  fields: TranslationField[];
  onSave?: () => void;
}

const TranslationEditor: React.FC<TranslationEditorProps> = ({
  section,
  fields,
  onSave
}) => {
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTranslations();
  }, [section, fields]);

  const loadTranslations = async () => {
    setLoading(true);
    try {
      const translationData: Record<string, string> = {};
      
      for (const field of fields) {
        const { data } = await translationsApi.getTranslation(field.key);
        translationData[field.key] = data?.value || '';
      }
      
      setTranslations(translationData);
    } catch (err) {
      setError('Tõlgete laadimine ebaõnnestus');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const translationUpdates = Object.entries(translations).map(([key, value]) => ({
        key,
        value,
        locale: 'et'
      }));

      const { error } = await translationsApi.upsertTranslations(translationUpdates);

      if (error) {
        setError('Salvestamine ebaõnnestus');
        return;
      }

      onSave?.();
    } catch (err) {
      setError('Salvestamine ebaõnnestus');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (key: string, value: string) => {
    setTranslations(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {fields.map((field) => (
        <div key={field.key}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {field.label}
          </label>
          {field.type === 'textarea' ? (
            <textarea
              value={translations[field.key] || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          ) : (
            <input
              type="text"
              value={translations[field.key] || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          )}
        </div>
      ))}

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Salvestamine...
            </>
          ) : (
            'Salvesta muudatused'
          )}
        </button>
      </div>
    </div>
  );
};

export default TranslationEditor;